import { useState, useCallback } from "react";
import useSWR from "swr";
import useUser from "../context/userHook";
import useMondayContext from "../context/useMondayContext";
import { getConsentStatus, recordConsent } from "../../utils/requests/consentRequest";
import { buildConsentPayload, deriveConsentState } from "../../utils/consentModel";
import { CONSENT_SOURCE_ONBOARDING } from "../../data/constants/legalPolicies";
import logger from "../../lib/logger";
import type {
  ConsentSelections,
  ConsentStatusItem,
  RecordConsentResult,
} from "../../utils/consentTypes";

export const useConsent = () => {
  const { userId } = useUser();
  const { appVersion } = useMondayContext();

  const { data, error, isLoading, mutate } = useSWR<ConsentStatusItem[]>(
    userId ? `consent-status:${userId}` : null,
    getConsentStatus,
  );

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<Error | null>(null);

  const consentState = deriveConsentState(data);

  const submitConsent = useCallback(
    async (
      selections: ConsentSelections,
      source: string = CONSENT_SOURCE_ONBOARDING,
    ): Promise<RecordConsentResult> => {
      setSubmitting(true);
      setSubmitError(null);
      try {
        const body = buildConsentPayload(selections, source, appVersion ?? "");
        const result = await recordConsent(body);
        await mutate();
        return result;
      } catch (err) {
        logger.error("Consent record failed", { error: String(err) });
        setSubmitError(err as Error);
        throw err;
      } finally {
        setSubmitting(false);
      }
    },
    [mutate, appVersion],
  );

  return {
    statusData: data,
    isLoading,
    statusError: error,
    needsConsent: consentState.needsConsent,
    consentState,
    submitConsent,
    submitting,
    submitError,
    mutate,
  };
};

export default useConsent;
